import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Facets, HaupiaResponseRowHighlighting, HaupiaResponseRowResult, HaupiaService } from './haupia.service';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { Address } from '../components/address/address.component';
import { HttpClient } from '@angular/common/http';
import { ContactDetails } from '../components/contact-details/contact-details.component';
import { Contact } from '../components/contact/contact.component';
import { LanguageUrls } from '../types/page';

const PREPARED_SEARCH_NAME_LOCATIONS = 'PS_LOCATIONS';

export enum LocationType {
  Virtual = 'virtual',
  Physical = 'physical',
}

export enum LocationFinderSortField {
  City = 'city_sort',
  Name = 'name_sort',
  Distance = 'geodist()', // TODO: this does not work, remove && false once this is resolved
}

interface BaseLocationHaupiaResponseRowResult extends HaupiaResponseRowResult {
  name: [string];
  nameLocalLanguage?: [string];
  lat: [string];
  long: [string];
  category_keyword: [string];
  countryCode_keyword: [string];
  locationType_keyword: [LocationType];
}
interface PhysicalLocationHaupiaResponseRowResult extends BaseLocationHaupiaResponseRowResult {
  locationType_keyword: [LocationType.Physical];
  state?: [string];
  stateLocalLanguage?: [string];
  zip?: [string];
  city: [string];
  cityLocalLanguage?: [string];
  street: [string];
  streetLocalLanguage?: [string];
  streetNo?: [string];
  addressExtra?: [string];
  addressExtraLocalLanguage?: [string];
  competence_keyword?: string[];
  network_keyword?: [string];
}
interface VirtualLocationHaupiaResponseRowResult extends BaseLocationHaupiaResponseRowResult {
  locationType_keyword: [LocationType.Virtual];
}
type LocationHaupiaResponseRowResult = PhysicalLocationHaupiaResponseRowResult | VirtualLocationHaupiaResponseRowResult;

function isPhysicalLocationResponse(response: LocationHaupiaResponseRowResult): response is PhysicalLocationHaupiaResponseRowResult {
  return response.locationType_keyword[0] === LocationType.Physical;
}

type LocationHaupiaResponseRowHighlighting = HaupiaResponseRowHighlighting

type LocationHaupiaResponseFacets = {
  locationType: LocationType;
  competence: string;
  category: string;
  countryCode: string;
}

type LocationDetails = ContactDetails & {
  contacts: Contact[];
}

export type BaseLocation = {
  id: string;
  index: number;
  name: string;
  nameLocalLanguage?: string;
  category: string;
  latitude: number;
  longitude: number;
  countryCode: string;
  details$: Observable<LocationDetails>;
}

export type PhysicalLocation = {
  locationType: LocationType.Physical;

  address: Address;

  competencies?: string[];
  network?: string;
} & BaseLocation;

export type VirtualLocation = {
  locationType: LocationType.Virtual;
} & BaseLocation;

export type Location = PhysicalLocation | VirtualLocation;

export type LocationResults = {
  numRows: number | null;
  visibleNumRows: number | null;
  locations: Location[];
  facets: Facets<LocationHaupiaResponseFacets>;
}

@Injectable({
  providedIn: 'root',
})
export class LocationFinderService {

  private localeId = inject(LOCALE_ID);

  private locationPreparedSearch = inject(HaupiaService).prepareSearch<
    LocationHaupiaResponseRowResult,
    LocationHaupiaResponseRowHighlighting,
    LocationHaupiaResponseFacets
  >(PREPARED_SEARCH_NAME_LOCATIONS, 99999, false);

  private httpClient = inject(HttpClient);

  private boundsFilter$ = new BehaviorSubject<google.maps.LatLngBounds | undefined>(undefined);

  public results$ = combineLatest([
    this.boundsFilter$.pipe(
      distinctUntilChanged(),
    ),
    this.locationPreparedSearch.results$,
  ]).pipe(
    map(([boundsFilter, results]): LocationResults => {
      let locations = results.rows.map((row, index) => {
        const result = row.result;
        const languageUrls: LanguageUrls = JSON.parse(row.result.link);
        const baseLocation: BaseLocation = {
          id: result.id,
          index: index,
          name: result.name[0],
          nameLocalLanguage: result.nameLocalLanguage?.[0],
          latitude: parseFloat(result.lat[0]),
          longitude: parseFloat(result.long[0]),
          category: result.category_keyword[0],
          countryCode: result.countryCode_keyword[0],
          details$: this.httpClient.get<LocationDetails>(languageUrls[this.localeId]).pipe(
            shareReplay(1),
          ),
        };
        if (isPhysicalLocationResponse(result)) {
          return {
            ...baseLocation,
            locationType: result.locationType_keyword[0],

            address: {
              countryCode: result.countryCode_keyword[0],
              postalCode: result.zip?.[0],
              administrativeArea: result.stateLocalLanguage?.[0],
              administrativeAreaLatin: result.state?.[0],
              locality: result.cityLocalLanguage?.[0],
              localityLatin: result.city[0],
              streetNo: result.streetNo?.[0],
              addressLine1: result.streetLocalLanguage?.[0],
              addressLine1Latin: result.street[0],
              addressLine2: result.addressExtraLocalLanguage?.[0],
              addressLine2Latin: result.addressExtra?.[0],
            },
            competencies: result.competence_keyword,
            network: result.network_keyword?.[0],
          } satisfies PhysicalLocation;
        } else {
          return {
            ...baseLocation,
            locationType: result.locationType_keyword[0],
          } satisfies VirtualLocation;
        }
      });

      let visibleNumRows = null;

      if (boundsFilter !== undefined) {
        const initialRows = locations.length;
        locations = locations.filter((location) => {
          return boundsFilter.contains({
            lat: location.latitude,
            lng: location.longitude,
          });
        });

        if (results.numRows) {
          visibleNumRows = results.numRows - (initialRows - locations.length);
        }
      }

      return {
        numRows: results.numRows,
        visibleNumRows: visibleNumRows,
        locations: locations,
        facets: results.facets,
      }
    }),
    shareReplay(1),
  );

  public setPos(coordinates: string, radius: number): void {
    this.locationPreparedSearch.setPos(coordinates, radius);
  }

  public removePos(): void {
    this.locationPreparedSearch.removePos();
  }

  public setRadius(radius: number): void {
    this.locationPreparedSearch.setRadius(radius);
  }

  public setQuery(query: string): void {
    this.locationPreparedSearch.setQuery(query);
  }

  public setSort(sortField: LocationFinderSortField): void {
    this.locationPreparedSearch.setSorting(sortField as string);
  }

  public setCategories(categories: string[]): void {
    this.locationPreparedSearch.setFacetFilterManually('category', categories);
  }

  public setCountryCode(countryCode: string | null): void {
    this.locationPreparedSearch.setFacetFilterManually('countryCode', countryCode ? [countryCode] : []);
  }

  public setCompetencies(competencies: string[]): void {
    this.locationPreparedSearch.setFacetFilterManually('competence', competencies);
  }

  public setType(types: LocationType[]): void {
    this.locationPreparedSearch.setFacetFilterManually('locationType', types);
  }

  public setBoundsFilter(bounds?: google.maps.LatLngBounds): void {
    this.boundsFilter$.next(bounds);
  }

}
